import type { ReactNode, FC } from "react";
import { useState, useRef, useCallback, useMemo } from "react";

import type { Dayjs } from "dayjs";

import { customRangeFormat, customRangeWithDayFormat } from "@similarweb/pro-logic-components";
import type { IDropdownItem, IMaxSelectableDurationRange, IPreset } from "@similarweb/ui-components";
import {
    DurationSelectorStateless,
    EGranularity,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { isPeriodOverPeriodToggleSupported } from "app/components/duration-selector/period-over-period/isPeriodOverPeriodToggleSupported";
import type { IFilterBaseProps } from "app/components/filters-bar/FiltersBase";
import TrialHook from "app/components/React/TrialHook/TrialHook";
import {
    convertInitialPresetToDuration,
    getCompareDurationSelected,
    getSelectedDurationPresetId,
    isCompareDuration,
} from "app/components/duration-selector/DurationSelectorUtils";
import { ECompareType } from "app/components/duration-selector/DurationSelectorTypes";
import { default as DurationService } from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";
import { PeriodOverPeriodService, periodOverPeriodService } from "app/services/PeriodOverPeriodService";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";
import { allTrackers } from "app/services/track/track";
import UIComponentStateService from "app/services/UIComponentStateService";
import { isNoTouchOrTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { UnlockModalHookWrapperBind } from "pro-features/components/Modals/src/UnlockModal/UnlockModalHookWrapper";

import { DurationSelectorButton } from "./DurationSelectorButton";

type ID = string;
type DurationObject = { isDaily: boolean; from: Dayjs; to: Dayjs };
type PresetExtendObject = { locked?: boolean; displayText?: string };
type Presets<T extends DurationObject = DurationObject> = Array<IDropdownItem<T> & IPreset<ID> & PresetExtendObject>;
type Preset = Presets[number];

type IDurationTypeForTracking = "CUSTOM_RANGE" | "YEAR_OVER_YEAR" | "PRE_DEFINED_DURATION" | "PERIOD_OVER_PERIOD";

interface IDurationSelectorSimpleProps extends IFilterBaseProps {
    minDate?: Dayjs;
    maxDate?: Dayjs;
    minDailyDate?: Dayjs;
    maxDailyDate?: Dayjs;
    isDisabled: boolean;
    presets?: Presets;
    weeklyPresets?: Presets;
    initialPreset: ID;
    initialComparedDuration?: string | boolean;
    onSubmit: (
        duration: string,
        comparedDuration: boolean | string,
        durationTypeForTracking?: IDurationTypeForTracking,
    ) => void;
    // Uses date duration and limits from the module config
    componentConfigId?: string;
    keys?: string | string[];
    compareAllowed?: boolean;
    compareLabel?: string;
    compareDisabledTooltipText?: string;
    hasPermissionsLock?: boolean;
    disableCalendar?: boolean;
    disableFooter?: boolean;
    isWeeklyDataAvailable?: boolean;
    showWeeklyToggle?: boolean;
    weeklyCalendarLocked?: ReactNode;
    isShowPoPDropdownLockModal?: boolean;
    periodOverPeriodCompareType?: IPeriodOverPeriodCompareType;
    isDaily?: boolean;
    presentCustomIndicator?: boolean;
    allowSingleDayPresentation?: boolean;
    isSingleDaySelection?: boolean;
    isSingleMonthSelection?: boolean;
    compareEnabledManually?: boolean;
    onGranularityToggleClick?: (item: EGranularity) => void;
    withoutPresets?: boolean;
    maxDurationRange?: IMaxSelectableDurationRange;
    height?: string;
}

function formatDuration(duration: DurationObject, granularity = EGranularity.MONTHLY) {
    let format = customRangeFormat;
    if (granularity === EGranularity.WEEKLY) {
        format = customRangeWithDayFormat;
    }
    return `${duration.from.format(format)}-${duration.to.format(format)}`;
}

const LOCAL_STORAGE_ID = "duration_selector_bubble";
const getUserLanguage = (): string => (swSettings.components?.Home?.resources?.Language as string) ?? "en-US";

const DurationSelectorSimple: FC<IDurationSelectorSimpleProps> = (props) => {
    const {
        height = "60px",
        weeklyPresets = [],
        isWeeklyDataAvailable = false,
        weeklyCalendarLocked = null,
        initialPreset = "",
        presets = [],
        presentCustomIndicator = true,
        allowSingleDayPresentation = false,
        compareAllowed = false,
        keys = [],
        disableFooter,
        disableCalendar,
        componentConfigId,
        isDisabled = false,
        // children,
        compareDisabledTooltipText,
        compareEnabledManually,
        compareLabel,
        initialComparedDuration = false,
        isShowPoPDropdownLockModal = false,
        periodOverPeriodCompareType = "",
        isDaily = false,
        onGranularityToggleClick = () => {},
        maxDurationRange,
        minDate,
        maxDate,
        minDailyDate,
        maxDailyDate,
        hasPermissionsLock,
        isSingleDaySelection,
        isSingleMonthSelection,
        showWeeklyToggle,
        presentNotificationIndicator,
        presentNotificationTooltipKey,
    } = props;

    const [popOverOpen, setPopOverOpen] = useState(false);

    // Refs for storing last presets
    const lastWeeklyPreset = useRef<string | number>("");
    const lastMonthlyPreset = useRef<string | number>("");

    // Helper function to get selected preset
    const getSelectedPreset = useCallback(() => {
        const isCustom = initialPreset.toString().indexOf("-") !== -1;
        const presetsToIterate = isDaily ? weeklyPresets : presets;
        const noAvailablePresets = presetsToIterate.every((p) => p.locked);
        const selectedPreset = isCustom || noAvailablePresets ? null : initialPreset;
        return selectedPreset;
    }, [initialPreset, isDaily, presets, weeklyPresets]);

    // Helper function to get compare type list
    const getCompareTypeList = useCallback(
        (
            initialPresetID: Preset["id"],
            incomingKeys: unknown,
            incomingComponentName: string,
            symbol: ID,
        ): Array<{
            id: ID;
            text: string;
            value: number;
            disabled: boolean;
        }> => {
            return periodOverPeriodService
                .getPeriodOverPeriodDropdownItems(initialPresetID, incomingKeys, incomingComponentName, false, symbol)
                .map(({ id, text, disabled }, index) => {
                    return {
                        id,
                        text,
                        value: index,
                        disabled,
                    };
                });
        },
        [],
    );

    // Helper function to check if compare duration is enabled
    // const isCompareDurationEnabled = useCallback(
    //     (
    //         duration: DurationObject,
    //         isWindow: boolean,
    //         _initialComparedDuration: string | boolean,
    //         incomingKeys: string | string[],
    //         incomingComponentName: string,
    //         isCustom = false,
    //         selectedGranularity?: EGranularity,
    //         compareDurationSelected?: { primary: DurationObject; secondary: DurationObject | null } | null,
    //     ) => {
    //         // currently no compare allowed on daily data
    //         if (duration.isDaily) {
    //             return false;
    //         }
    //         let durationSymbol: string;

    //         if (isCustom) {
    //             durationSymbol = formatDuration(duration, selectedGranularity || EGranularity.MONTHLY);
    //         } else {
    //             durationSymbol = DurationServiceItem.getDiffSymbol(
    //                 duration.from,
    //                 duration.to,
    //                 isWindow ? "days" : "months",
    //             ) as string;
    //         }

    //         const durationSymbol2 = DurationServiceItem.getDiffSymbol(
    //             duration.from,
    //             duration.to,
    //             isWindow ? "days" : "months",
    //         ) as string;

    //         return periodOverPeriodService.periodOverPeriodEnabled(
    //             durationSymbol,
    //             compareDurationSelected,
    //             incomingKeys,
    //             incomingComponentName,
    //             durationSymbol2,
    //         );
    //     },
    //     [],
    // );

    // Helper function to get initial state
    const getInitialState = useCallback(() => {
        const compareSelected = !!initialComparedDuration || Boolean(periodOverPeriodCompareType);
        const selectedPreset = getSelectedPreset();
        let compareTypesSelected: ID = "";
        let compareTypeList: Array<{
            id: ID;
            text: string;
            value: number;
            disabled: boolean;
        }> = [];
        let compareEnabled = false;
        const duration = convertInitialPresetToDuration(props.initialPreset, [
            ...presets,
            ...weeklyPresets,
        ]) as DurationObject;

        if (compareAllowed) {
            compareTypeList = getCompareTypeList(initialPreset, keys, componentConfigId, initialPreset);
            // compare to previous year by default
            const isComparedYear =
                (initialComparedDuration === false || initialComparedDuration === "12m") &&
                !compareTypeList.find((item) => item.id === PeriodOverPeriodService.COMPARE_TYPES.YEAR)?.disabled;

            compareTypesSelected = (
                isComparedYear
                    ? PeriodOverPeriodService.COMPARE_TYPES.YEAR
                    : PeriodOverPeriodService.COMPARE_TYPES.PERIOD
            ) as ID;

            if (periodOverPeriodCompareType) {
                compareTypesSelected = periodOverPeriodCompareType as ID;
            }

            compareEnabled = false;
        }

        const compareDurationSelected: {
            primary: DurationObject;
            secondary: DurationObject;
        } = getCompareDurationSelected(
            //from preset, from custom, + compare => convert to duration
            convertInitialPresetToDuration(initialPreset, [...presets, ...weeklyPresets]),
            compareSelected && compareEnabled,
            compareTypesSelected,
        );

        const isBubbleOpen = UIComponentStateService.getItem(LOCAL_STORAGE_ID, "localStorage", true) !== "false";

        return {
            compareSelected,
            selectedPreset,
            compareDurationSelected,
            compareTypesSelected,
            compareTypeList,
            compareEnabled: false,
            isOpen: false,
            isBubbleOpen,
            selectedGranularity: isDaily || duration.isDaily ? EGranularity.WEEKLY : EGranularity.MONTHLY,
            sameDateClickedByUser: false,
        };
    }, [
        isDaily,
        initialComparedDuration,
        periodOverPeriodCompareType,
        getSelectedPreset,
        props.initialPreset,
        presets,
        weeklyPresets,
        compareAllowed,
        initialPreset,
        getCompareTypeList,
        keys,
        componentConfigId,
    ]);

    // State
    const [state, setState] = useState(() => getInitialState());

    // Helper functions
    const getDurationTypeForTracking = useCallback((): IDurationTypeForTracking => {
        const {
            selectedPreset,
            compareSelected: isPeriodOverPeriod,
            compareTypesSelected: periodOverPeriodType,
        } = state;
        const isCustomRange = !selectedPreset;
        if (isPeriodOverPeriod) {
            return periodOverPeriodType === PeriodOverPeriodService.COMPARE_TYPES.YEAR
                ? "YEAR_OVER_YEAR"
                : "PERIOD_OVER_PERIOD";
        }
        if (isCustomRange) {
            return "CUSTOM_RANGE";
        }
        return "PRE_DEFINED_DURATION";
    }, [state]);

    const handleOnSubmit = useCallback(() => {
        const { selectedPreset, compareDurationSelected, compareSelected, compareTypesSelected } = state;
        const durationTypeForTracking = getDurationTypeForTracking();
        const granularity = isDaily ? EGranularity.WEEKLY : state.selectedGranularity;
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `Date Range/${selectedPreset ? selectedPreset : "Custom"}`,
        );
        props.onSubmit(
            selectedPreset ? selectedPreset : formatDuration(compareDurationSelected.primary, granularity),
            compareSelected
                ? compareTypesSelected === "previous year"
                    ? "12m"
                    : (DurationService.getDiffSymbol(
                          compareDurationSelected.primary.from,
                          compareDurationSelected.primary.to,
                          "months",
                      ) as string)
                : false,
            durationTypeForTracking,
        );
        setPopOverOpen(false);
    }, [state, getDurationTypeForTracking, props, isDaily]);

    const trackTypeToggle = useCallback((type: string) => {
        allTrackers.dispatchTrackEvent("duration.type.toggle", "switch", "", { type });
    }, []);

    const clearCompare = useCallback((cb?: () => void) => {
        setState((prevState) => ({
            ...prevState,
            compareSelected: false,
            compareDurationSelected: getCompareDurationSelected(
                prevState.compareDurationSelected.primary,
                false,
                prevState.compareTypesSelected,
            ),
        }));
        if (cb) {
            cb();
        }
    }, []);

    const setStateAfterPresetChange = useCallback(
        (preset: Preset, compareEnabled: boolean) => {
            const newCompareTypeList = getCompareTypeList(preset.id, keys, componentConfigId, preset.id);
            const isPreviousYearDisabled = newCompareTypeList.some(
                ({ id, disabled }) => id === ECompareType.PreviousYear && disabled,
            );
            const { compareTypesSelected } = state;
            const shouldOverrideCompareTypesSelected =
                isPreviousYearDisabled && compareTypesSelected === ECompareType.PreviousYear;

            setState((prevState) => ({
                ...prevState,
                compareDurationSelected: getCompareDurationSelected(
                    preset.value,
                    isCompareDuration(prevState.compareSelected, prevState.compareEnabled, compareAllowed),
                    prevState.compareTypesSelected,
                ),
                compareTypeList: newCompareTypeList,
                selectedPreset: preset.id,
                compareEnabled,
                compareTypesSelected: shouldOverrideCompareTypesSelected
                    ? ECompareType.PreviousPeriod
                    : compareTypesSelected,
            }));

            if (disableFooter && disableCalendar) {
                setTimeout(handleOnSubmit, 0);
            }
        },
        [
            getCompareTypeList,
            keys,
            componentConfigId,
            state,
            disableFooter,
            disableCalendar,
            compareAllowed,
            handleOnSubmit,
        ],
    );

    const onPresetSelect = useCallback(
        (shouldTrack = false) =>
            (preset: Presets[number]) => {
                if (props.withoutPresets) {
                    return;
                }

                if (shouldTrack) {
                    allTrackers.dispatchTrackEvent("duration.preset.select", "click", "", {
                        preset: preset.id,
                    });
                }

                // save the preset to we can use it when granularity changed
                switch (state.selectedGranularity) {
                    case EGranularity.WEEKLY:
                        lastWeeklyPreset.current = preset.id;
                        break;
                    case EGranularity.MONTHLY:
                        lastMonthlyPreset.current = preset.id;
                }

                if (preset.locked) {
                    openHooksModalV5("WebAllowedDuration");
                    setPopOverOpen(false);

                    allTrackers.dispatchTrackEvent(
                        "hook/Contact Us/Pop up",
                        "click",
                        `Time Filter/${preset ? preset.id : "Custom"}`,
                    );
                    return;
                }

                const compareEnabled = false;

                if (state.compareEnabled !== compareEnabled) {
                    clearCompare(() => {
                        //callback required because setStateAfterPresetChange() depends on changed state after clearCompare()
                        setStateAfterPresetChange(preset, compareEnabled);
                    });
                } else {
                    setStateAfterPresetChange(preset, compareEnabled);
                }
            },
        [props, state.selectedGranularity, state.compareEnabled, clearCompare, setStateAfterPresetChange],
    );

    const onGranularityChange = useCallback(
        (index: EGranularity) => {
            onGranularityToggleClick(index);

            let newState: Partial<typeof state> = {};

            if (index === EGranularity.WEEKLY) {
                trackTypeToggle("week");
                newState = {
                    selectedGranularity: EGranularity.WEEKLY,
                    compareSelected: false,
                    compareEnabled: false,
                };
            } else {
                trackTypeToggle("month");
                newState = {
                    selectedGranularity: EGranularity.MONTHLY,
                };
            }

            setState((prevState) => ({ ...prevState, ...newState }));

            // Handle preset selection after state update
            setTimeout(() => {
                if (index === EGranularity.WEEKLY) {
                    // try to find the initial preset in the weekly presets
                    // if not, take the first one as default
                    const weeklyPreset = weeklyPresets.find((p) => p.id === lastWeeklyPreset.current);
                    onPresetSelect(false)(weeklyPreset || weeklyPresets[0]);
                } else if (initialPreset.toString().indexOf("-") === -1) {
                    // try to find the initial preset in the presets
                    // if not, take the 3m as default
                    const monthlyPreset = presets.find((p) => p.id === lastMonthlyPreset.current);
                    onPresetSelect(false)(monthlyPreset || presets.find((p) => p.id === "3m"));
                }
            }, 0);
        },
        [initialPreset, onGranularityToggleClick, onPresetSelect, presets, trackTypeToggle, weeklyPresets],
    );

    const activePresets = useMemo(() => {
        if (state.selectedGranularity === EGranularity.WEEKLY) {
            return weeklyPresets;
        } else {
            return presets;
        }
    }, [state.selectedGranularity, weeklyPresets, presets]);

    const onSameDateClickedByUser = useCallback((sameDateClickedByUser: boolean): void => {
        setState((prevState) => ({
            ...prevState,
            sameDateClickedByUser: sameDateClickedByUser,
        }));
    }, []);

    const onCompareToggle = useCallback(() => {
        allTrackers.trackEvent(
            "Checkbox",
            state.compareSelected ? "Remove" : "Add",
            `Date range/compare/${state.compareTypesSelected}`,
        );
        setState((prevState) => ({
            ...prevState,
            compareSelected: !prevState.compareSelected,
            compareDurationSelected: getCompareDurationSelected(
                prevState.compareDurationSelected.primary,
                !isCompareDuration(prevState.compareSelected, prevState.compareEnabled, compareAllowed),
                prevState.compareTypesSelected,
            ),
        }));
    }, [state.compareSelected, state.compareTypesSelected, compareAllowed]);

    const onDurationChange = useCallback(
        (duration: DurationObject) => {
            const isCustom = true; // seems like it is called just for custom dates
            const compareDurationSelected = getCompareDurationSelected(
                duration,
                isCompareDuration(state.compareSelected, state.compareEnabled, compareAllowed),
                state.compareTypesSelected,
            );
            const newState: {
                compareTypesSelected: ID | null;
                compareDurationSelected: { primary: DurationObject; secondary: DurationObject | null };
                compareEnabled: boolean;
                compareSelected: boolean;
                compareTypeList: Array<{
                    id: ID;
                    text: string;
                    value: number;
                    disabled: boolean;
                }>;
                selectedPreset?: ID;
            } = {
                compareTypesSelected: state.compareTypesSelected,
                compareDurationSelected,
                compareEnabled: false,
                compareSelected: false,
                compareTypeList: [],
            };

            if (newState.compareEnabled) {
                const durationSymbol = DurationService.getDiffSymbol(duration.from, duration.to, "days") as string;
                let durationFormatted: string;
                if (isCustom) {
                    durationFormatted = formatDuration(duration, state.selectedGranularity);
                }
                newState.compareTypeList =
                    getCompareTypeList(durationFormatted, keys, componentConfigId, durationSymbol) ?? [];

                const disabledItems = newState.compareTypeList.filter(({ disabled }) => disabled);
                // in case if we have selected period which valid for period but year selected
                // we'll select automatically wrong period
                if (disabledItems.length > 0) {
                    if (disabledItems.length === 2) {
                        if (newState.compareDurationSelected) {
                            newState.compareDurationSelected.secondary = null;
                        }
                        newState.compareSelected = false;
                    } else {
                        newState.compareTypesSelected = newState.compareTypeList.find((item) => !item.disabled)?.id;
                        newState.compareDurationSelected = getCompareDurationSelected(
                            duration,
                            isCompareDuration(state.compareSelected, state.compareEnabled, compareAllowed),
                            newState.compareTypesSelected,
                        );
                    }
                }
            } else {
                if (newState.compareDurationSelected) {
                    newState.compareDurationSelected.secondary = null;
                }
                newState.compareSelected = false;
            }

            newState.selectedPreset = getSelectedDurationPresetId(
                compareDurationSelected,
                state.selectedGranularity === EGranularity.WEEKLY ? weeklyPresets : presets,
                "day",
            )?.toString();

            setState((prevState) => ({
                ...prevState,
                ...newState,
                compareTypesSelected: String(newState.compareTypesSelected ?? prevState.compareTypesSelected),
            }));
        },
        [
            state.compareSelected,
            state.compareEnabled,
            state.compareTypesSelected,
            state.selectedGranularity,
            compareAllowed,
            keys,
            componentConfigId,
            weeklyPresets,
            presets,
            getCompareTypeList,
        ],
    );

    const onCompareSelect = useCallback(
        <T extends Presets[number]>(compareType: T) => {
            allTrackers.dispatchTrackEvent("Drop Down", "Click", `Date range/compare/${compareType.id}`);
            setState((prevState) => ({
                ...prevState,
                compareTypesSelected: compareType.id,
                compareDurationSelected: getCompareDurationSelected(
                    prevState.compareDurationSelected.primary,
                    isCompareDuration(prevState.compareSelected, prevState.compareEnabled, compareAllowed),
                    compareType.id,
                ),
            }));
        },
        [compareAllowed],
    );

    const onDropdownButtonClick = useCallback(() => {
        allTrackers.dispatchTrackEvent("Drop Down", "Open", "Date range/compare");
    }, []);

    const footerComponent = useCallback(() => {
        if (isNoTouchOrTrialOrAfterTrial()) {
            return (
                <TrialHook
                    text="trial_hook.duration.text"
                    buttonText="trial_hook.duration.button.text"
                    iconName="daily-ranking"
                    label="Duration Selector"
                    onCtaClick={() => {
                        openHooksModalV5("WebAllowedDuration");
                    }}
                />
            );
        }

        return null;
    }, []);

    return (
        <Popover
            placement="bottom-end"
            open={popOverOpen}
            onOpenChange={(isOpen) => !isDisabled && setPopOverOpen(isOpen)}
        >
            <PopoverTrigger
                asChild
                disabled={isDisabled}
            >
                <DurationSelectorButton
                    height={height}
                    isDisabled={!!isDisabled}
                    presets={[...presets, ...weeklyPresets]}
                    initialPreset={initialPreset}
                    isCompare={!!initialComparedDuration}
                    presentCustomIndicator={presentCustomIndicator}
                    allowSingleDayPresentation={allowSingleDayPresentation}
                    presentNotificationIndicator={presentNotificationIndicator}
                    presentNotificationTooltipKey={presentNotificationTooltipKey}
                />
            </PopoverTrigger>
            <PopoverContent style={{ padding: 0 }}>
                <DurationSelectorStateless
                    locale={getUserLanguage()}
                    presets={activePresets}
                    selectedPreset={state.selectedPreset}
                    presetsLabel="Date Range"
                    cancelButtonText="Cancel"
                    submitButtonText="Apply"
                    customPresetText="Custom"
                    compareAllowed={compareAllowed ? !isPeriodOverPeriodToggleSupported() : null}
                    compareLabel={compareLabel}
                    compareDisabledTooltipText={compareDisabledTooltipText}
                    compareSelected={state.compareSelected}
                    compareEnabled={compareEnabledManually ? compareEnabledManually : state.compareEnabled}
                    compareTypesList={state.compareTypeList}
                    compareTypesSelected={state.compareTypesSelected}
                    compareDurationSelected={state.compareDurationSelected}
                    minDate={minDate}
                    maxDate={maxDate}
                    minDailyDate={minDailyDate}
                    maxDailyDate={maxDailyDate}
                    compareToggle={onCompareToggle}
                    compareSelect={onCompareSelect}
                    durationChange={onDurationChange}
                    presetSelect={onPresetSelect(true)}
                    selectorSubmit={handleOnSubmit}
                    hasPermissionsLock={hasPermissionsLock}
                    disableCalendar={disableCalendar}
                    disableFooter={disableFooter}
                    renderContactUs={() => null}
                    itemWrapper={
                        isShowPoPDropdownLockModal
                            ? // oxlint-disable-next-line react/capitalized-calls -- UnlockModalHookWrapperBind is a HOC factory (returns a render-prop function), not a component; PascalCase is intentional here
                              UnlockModalHookWrapperBind({
                                  location: `${LocationService.getCurrentLocationTracking()}/Time Ranges`,
                                  featureKey: "WebAllowedDuration",
                              })
                            : null
                    }
                    onDropdownButtonClick={onDropdownButtonClick}
                    footerComponent={footerComponent}
                    onGranularityToggleClick={onGranularityChange}
                    selectedGranularity={state.selectedGranularity}
                    showWeeklyToggle={showWeeklyToggle}
                    isWeeklyDataAvailable={isWeeklyDataAvailable}
                    weeklyCalendarLocked={weeklyCalendarLocked}
                    useDailyCalendar={isDaily}
                    isSingleDaySelection={isSingleDaySelection}
                    isSingleMonthSelection={isSingleMonthSelection}
                    onSameDateClickedByUser={onSameDateClickedByUser}
                    maxDurationRange={maxDurationRange}
                    sameDateClickedByUser={state.sameDateClickedByUser}
                />
            </PopoverContent>
        </Popover>
    );
};

export default DurationSelectorSimple;
